import styled, { css } from 'styled-components';
import { getSpacingToken, getToken } from '@optimus-web/common';

export const CARD_SPACINGS = ['0', '100', '200', '300', '400'] as const;
export type CardSpacing = (typeof CARD_SPACINGS)[number];

export const CARD_VARIANTS = ['default', 'subtle', 'highlighted'];
export type CardVariants = (typeof CARD_VARIANTS)[number];

export interface CardProps {
    cornerRadius?: 'small' | 'medium' | 'large';
    outline?: boolean;
    overflowHidden?: boolean;
    padding?: CardSpacing;
    variant?: CardVariants;
}

const getBackgroundColor = ({ variant }: { variant?: CardProps['variant'] }) => {
    switch (variant) {
        case 'default':
            return getToken('backgroundStaticFlat');
        case 'subtle':
            return getToken('backgroundInteractiveNeutralDefault');
        case 'highlighted':
            return getToken('backgroundInteractiveSecondaryDefault');
        default:
            return getToken('backgroundStaticFlat');
    }
};

const getBorderRadius = ({ cornerRadius = 'medium' }: { cornerRadius?: CardProps['cornerRadius'] }) => {
    switch (cornerRadius) {
        case 'small':
            return getToken('borderRadius100');
        case 'medium':
            return getToken('borderRadius200');
        case 'large':
            return getToken('borderRadius300');
    }
};

export const Card = styled.div<CardProps>`
    box-sizing: border-box;
    background-color: ${getBackgroundColor};
    border-radius: ${getBorderRadius}px;
    ${({ padding }) =>
        padding &&
        css`
            padding: ${getSpacingToken(padding)}px;
        `}
    ${({ outline }) =>
        outline &&
        css`
            border: ${getToken('borderWidth150')}px solid ${getToken('borderStaticPrimary')};
        `}
    ${({ overflowHidden }) =>
        overflowHidden &&
        css`
            overflow: hidden;
        `}
`;

Card.defaultProps = {
    outline: true,
    padding: '0',
    cornerRadius: 'small',
    variant: 'default',
};

interface CardContentProps {
    padding?: CardSpacing;
    highlighted?: boolean;
}

export const CardContent = styled.div<CardContentProps>`
    background-color: ${({ highlighted }) =>
        highlighted ? getToken('backgroundInteractiveSecondaryDefault') : 'transparent'};
    ${({ padding }) =>
        padding &&
        css`
            padding: ${getSpacingToken(padding)}px;
        `}
    ${({ highlighted }) =>
        highlighted &&
        css`
            &:not(:first-child) {
                border-top: ${getToken('borderWidth150')}px solid ${getToken('borderStaticPrimary')};
            }
            &:not(:last-child) {
                border-bottom: ${getToken('borderWidth150')}px solid ${getToken('borderStaticPrimary')};
            }
        `}
`;
